import type { PlanEntry } from '@agentclientprotocol/sdk';
import type { ToolInput } from '@gitlab-org/tui';

const PLAN_ENTRY_STATUSES: ReadonlySet<string> = new Set<PlanEntry['status']>([
  'pending',
  'in_progress',
  'completed',
]);

// ACP's PlanEntry has no ACP-side surface for todo priority; the backend's
// TodoItem doesn't carry one either, so every entry gets the same constant.
const PLAN_ENTRY_PRIORITY: PlanEntry['priority'] = 'medium';

export interface PlanEntriesResult {
  entries: PlanEntry[];
  /** User-facing warnings for todos whose status didn't match the known ACP statuses. */
  warnings: string[];
}

/**
 * Maps a `todo_write` tool call's todos to ACP plan entries, so clients can
 * render their native plan UI instead of a generic tool card. Returns
 * undefined for every other tool. Cancelled todos are dropped; each plan
 * update replaces the whole plan client-side, so they simply disappear.
 */
export function planEntriesFor(input: ToolInput): PlanEntriesResult | undefined {
  if (input.tool !== 'todo_write') return undefined;

  const warnings: string[] = [];
  const entries = input.todos
    .filter((todo) => todo.status !== 'cancelled')
    .map((todo) => {
      // The backend is a separate service reachable over the wire, so its
      // `status` string isn't guaranteed to match our compile-time union;
      // fall back to 'pending' rather than pass an invalid PlanEntry.status
      // through to the client.
      const known = PLAN_ENTRY_STATUSES.has(todo.status);
      if (!known) {
        warnings.push(`todo_write: unexpected status "${todo.status}", treating as pending`);
      }
      const status = known ? (todo.status as PlanEntry['status']) : 'pending';
      return { content: todo.description, priority: PLAN_ENTRY_PRIORITY, status };
    });

  return { entries, warnings };
}
